import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TaskCardRow } from '../components/TaskCardRow'
import { Button, CarDamageIcon, CardIcon, Heading, LostFuelCard, Modal, ReportDamage, Stack } from '@autoplan/web-ui'
import { InformationCard } from '../components/Cards/InformationCard'
import { MyTaskCard } from '../components/Cards/MyTaskCard'
import { MyCarCard } from '../components/Cards/MyCarCard'
import { NewsFeaturedCard } from '../components/Cards/NewsFeaturedCard'
import { NewsCard } from '../components/Cards/NewsCard'
import { useHomeContent, useTranslations, urlFor, type HomeContent } from '@autoplan/web-sanity'
import { detectLang, t } from '../utils/locale'

const ICON_MAP = {
  reportDamage: <CarDamageIcon />,
  lostCard: <CardIcon />,
}

export const Home = () => {
  const [isDamageModalOpen, setIsDamageModalOpen] = useState(false)
  const [isLostCardModalOpen, setIsLostCardModalOpen] = useState(false)
  const [activeModalTitle, setActiveModalTitle] = useState('')
  const navigate = useNavigate()
  const content = useHomeContent()
  const tSanity = useTranslations()
  const lang = detectLang()

  const page = content?.page ?? null
  const featuredArticle = content?.featuredArticle ?? null
  const articles = content?.articles ?? ([] as HomeContent['articles'])

  const handleInfoCardClick = (actionType?: string, modalTitle?: string) => {
    setActiveModalTitle(modalTitle ?? '')
    if (actionType === 'reportDamage') setIsDamageModalOpen(true)
    if (actionType === 'lostCard') setIsLostCardModalOpen(true)
  }

  return (
    <>
      <Stack spacing="4xl">
        <TaskCardRow />
        <div className="ap:grid ap:grid-cols-1 ap:gap-2xl ap:xl:grid-cols-2 ap:xl:items-start ap:xl:gap-3xl">
          <div className="ap:xl:col-start-2 ap:xl:row-start-1">
            {featuredArticle && (
              <NewsFeaturedCard
                title={featuredArticle.title ?? ''}
                imageSrc={
                  featuredArticle.image
                    ? (urlFor(featuredArticle.image)?.width(800).height(440).url() ?? undefined)
                    : undefined
                }
                onClick={() => featuredArticle.slug && navigate(`/articles/${featuredArticle.slug.current}`)}
              />
            )}
          </div>

          <Stack spacing="2xl" className="ap:xl:col-start-1 ap:xl:row-start-1 ap:xl:row-span-2">
            <Stack spacing="xl">
              <MyCarCard
                vehicle={{
                  regNumber: 'AB12345',
                  model: 'Model S',
                  brand: 'Tesla',
                  engineType: 'Electric',
                  imageUrl: '',
                  status: 'active',
                }}
              />
            </Stack>

            <Stack spacing="xl">
              <Heading size="md">{lang === 'en' ? 'My tasks' : 'Mine oppgaver'}</Heading>
              <MyTaskCard
                label={lang === 'en' ? 'Signing' : 'Signering'}
                subLabel={lang === 'en' ? 'Tasks sent for signing' : 'Oppgaver som er sent til signering'}
                taskCount={0}
                href="/"
              />
              <MyTaskCard
                label={lang === 'en' ? 'Add-ons' : 'Tilvalg'}
                subLabel={lang === 'en' ? 'Task to add options to car' : 'Oppgave om å gjøre tilvalg på bil'}
                taskCount={0}
                href="/"
              />
            </Stack>

            <Stack spacing="xl">
              <Heading size="md">{lang === 'en' ? 'Information' : 'Informasjon'}</Heading>
              <InformationCard
                icon={ICON_MAP.reportDamage}
                label={lang === 'en' ? 'Did you damage the car?' : 'Har du skadet bilen?'}
                buttonLabel={lang === 'en' ? 'Report damage' : 'Meld skade'}
                onClick={() => handleInfoCardClick('reportDamage', lang === 'en' ? 'Report damage' : 'Meld skade')}
              />
              <InformationCard
                icon={ICON_MAP.lostCard}
                label={lang === 'en' ? 'Did you lose your fuel card?' : 'Har du mistet drivstoffkortet?'}
                buttonLabel={lang === 'en' ? 'Lost card' : 'Mistet kort'}
                onClick={() => handleInfoCardClick('lostCard', lang === 'en' ? 'Lost card' : 'Mistet kort')}
              />
            </Stack>
          </Stack>

          {articles.length > 0 && (
            <Stack spacing="2xl" className="ap:xl:col-start-2 ap:xl:row-start-2">
              <Stack spacing="xl">
                <Heading size="lg">{t(page?.newsHeading, lang)}</Heading>
                {articles.map((article: HomeContent['articles'][number]) => (
                  <NewsCard
                    key={article._id}
                    title={article.title ?? ''}
                    introduction={''}
                    imageSrc={
                      article.image ? (urlFor(article.image)?.width(300).height(150).url() ?? undefined) : undefined
                    }
                    onClick={() => article.slug && navigate(`/articles/${article.slug.current}`)}
                  />
                ))}
                <Button variant="primary" onClick={() => navigate('/articles')}>
                  {tSanity('button.seeMore') ?? 'Se mer'}
                </Button>
              </Stack>
            </Stack>
          )}
        </div>
      </Stack>

      <Modal open={isDamageModalOpen} onOpenChange={setIsDamageModalOpen} title={activeModalTitle}>
        <ReportDamage
          navigateToHome={() => {}}
          data={{
            phoneNumbers: ['12345678', '87654321', '446257679'].map((number) => ({ number, type: 'claim' })),
            code: 'ABC123',
            homepage: 'fdsa.com',
          }}
        />
      </Modal>
      <Modal open={isLostCardModalOpen} onOpenChange={setIsLostCardModalOpen} title={activeModalTitle}>
        <LostFuelCard
          car={{
            registrationNumber: 'AB12345',
            additionalProducts: [
              { AgreementCode: 'FUEL_CARD', Description: 'Drivstoffkort', ReferenceNo: 'FUEL_CARD' },
            ],
          }}
          setShowLostFuelCard={setIsLostCardModalOpen}
          defaultData={{
            company: 'Autoplan Fleet',
            customerName: 'Ola Nordmann',
            address: 'Storgata 1',
            postalCode: '0155',
            postCity: 'Oslo',
          }}
          onSubmit={() => {}}
        />
      </Modal>
    </>
  )
}
